import * as React from 'react'
import PageLayout from '@/components/ui/page-layout'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { Link, useLocation } from 'react-router-dom'
import { SidebarContent, SidebarGroup, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider } from '@/components/ui/sidebar'
import { BarChart3, DollarSign, Users, Copy, UserX, Link2Off, CreditCard, FileText, HardDrive } from 'lucide-react'

type ReportsLayoutProps = {
    title?: string
    subtitle?: string
    reports?: { label: string; href: string }[]
    children: React.ReactNode
}

export default function ReportsLayout({ title = 'Reports', subtitle, children }: ReportsLayoutProps) {
    const location = useLocation()

    const commissionReports = [
        { label: 'Monthly Commission', href: '/reports/monthly-commission', icon: DollarSign },
    ]

    const subscriptionsReports = [
        { label: 'Chargebee subscriptions', href: '/reports/accounts-with-chargebee-subscriptions', icon: CreditCard },
        { label: 'Fastbound subscriptions', href: '/reports/accounts-with-fastbound-subscriptions', icon: FileText },
        { label: 'Duplicate subscriptions', href: '/reports/accounts-with-duplicate-subscriptions', icon: Copy },
        { label: 'Unsubscribed accounts', href: '/reports/accounts-without-subscriptions', icon: UserX },
        { label: 'Unmatched Chargebee', href: '/reports/chargebee-subscriptions-without-accounts', icon: Link2Off },
        { label: 'Unmatched Fastbound', href: '/reports/fastbound-diff', icon: FileText },
    ]

    const backupToolReports = [
        { label: 'Backup Tool Status', href: '/reports/backup-tool', icon: HardDrive },
    ]

    return (
        <PageLayout
            title={title}
            subtitle={subtitle}
            breadcrumbs={[
                { label: 'Reports', href: '/reports' },
                ...(location.pathname !== '/reports' ? [{ label: title, href: location.pathname }] : []),
            ]}
        >
            <div className='grid grid-cols-12 gap-6 lg:gap-8'>
                <aside className='col-span-12 lg:col-span-3 xl:col-span-2'>
                    <div className='sticky top-6'>
                        <Card className='border-border/50 shadow-sm overflow-hidden'>
                            <div className='bg-muted/50 p-4 border-b border-border/50'>
                                <h3 className='font-semibold flex items-center gap-2 text-sm'>
                                    <BarChart3 className='h-4 w-4' />
                                    Report Categories
                                </h3>
                            </div>
                            <SidebarProvider className='min-h-0'>
                                <SidebarContent className='p-2'>
                                <SidebarGroup>
                                        <SidebarGroupLabel className='px-2 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                                            Billing
                                        </SidebarGroupLabel>
                                    <SidebarMenu>
                                        {commissionReports.map((r) => {
                                            const active = location.pathname === r.href
                                            return (
                                                <SidebarMenuItem key={r.href}>
                                                        <SidebarMenuButton
                                                            asChild
                                                            isActive={active}
                                                            className={cn(
                                                                'w-full justify-start gap-3 px-3 py-2 h-auto transition-colors rounded-md',
                                                                active
                                                                    ? 'bg-primary/10 text-primary font-medium hover:bg-primary/15 hover:text-primary'
                                                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                                            )}
                                                        >
                                                            <Link to={r.href} className='flex items-start w-full'>
                                                                <r.icon
                                                                    className={cn(
                                                                        'h-4 w-4 mt-0.5 shrink-0',
                                                                        active ? 'text-primary' : 'text-muted-foreground'
                                                                    )}
                                                                />
                                                                <span className='text-sm leading-5'>{r.label}</span>
                                                        </Link>
                                                    </SidebarMenuButton>
                                                </SidebarMenuItem>
                                            )
                                        })}
                                        </SidebarMenu>
                                    </SidebarGroup>

                                    <div className='my-2 mx-2 border-t border-border/50' />

                                    <SidebarGroup>
                                        <SidebarGroupLabel className='px-2 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                                            Subscriptions
                                        </SidebarGroupLabel>
                                        <SidebarMenu>
                                            {subscriptionsReports.map((r) => {
                                                const active = location.pathname === r.href
                                                return (
                                                    <SidebarMenuItem key={r.href}>
                                                        <SidebarMenuButton
                                                            asChild
                                                            isActive={active}
                                                            className={cn(
                                                                'w-full justify-start gap-3 px-3 py-2 h-auto transition-colors rounded-md',
                                                                active
                                                                    ? 'bg-primary/10 text-primary font-medium hover:bg-primary/15 hover:text-primary'
                                                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                                            )}
                                                        >
                                                            <Link to={r.href} className='flex items-start w-full'>
                                                                <r.icon
                                                                    className={cn(
                                                                        'h-4 w-4 mt-0.5 shrink-0',
                                                                        active ? 'text-primary' : 'text-muted-foreground'
                                                                    )}
                                                                />
                                                                <span className='text-sm leading-5 break-words whitespace-normal'>
                                                                    {r.label}
                                                                </span>
                                                            </Link>
                                                        </SidebarMenuButton>
                                                    </SidebarMenuItem>
                                                )
                                            })}
                                    </SidebarMenu>
                                </SidebarGroup>

                                    <div className='my-2 mx-2 border-t border-border/50' />

                                    <SidebarGroup>
                                        <SidebarGroupLabel className='px-2 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                                            Backup Tool
                                        </SidebarGroupLabel>
                                        <SidebarMenu>
                                            {backupToolReports.map((r) => {
                                                const active = location.pathname === r.href
                                                return (
                                                    <SidebarMenuItem key={r.href}>
                                                        <SidebarMenuButton
                                                            asChild
                                                            isActive={active}
                                                            className={cn(
                                                                'w-full justify-start gap-3 px-3 py-2 h-auto transition-colors rounded-md',
                                                                active
                                                                    ? 'bg-primary/10 text-primary font-medium hover:bg-primary/15 hover:text-primary'
                                                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                                            )}
                                                        >
                                                            <Link to={r.href} className='flex items-start w-full'>
                                                                <r.icon
                                                                    className={cn(
                                                                        'h-4 w-4 mt-0.5 shrink-0',
                                                                        active ? 'text-primary' : 'text-muted-foreground'
                                                                    )}
                                                                />
                                                                <span className='text-sm leading-5'>{r.label}</span>
                                                            </Link>
                                                        </SidebarMenuButton>
                                                    </SidebarMenuItem>
                                                )
                                            })}
                                        </SidebarMenu>
                                    </SidebarGroup>
                            </SidebarContent>
                        </SidebarProvider>
                    </Card>
                    </div>
                </aside>
                <main className='col-span-12 lg:col-span-9 xl:col-span-10 min-w-0'>
                    <div className='animate-in fade-in slide-in-from-bottom-4 duration-500'>{children}</div>
                </main>
            </div>
        </PageLayout>
    )
}
